import { Popover as RadixPopover } from "radix-ui";
import { type KeyboardEvent, useId, useMemo, useRef, useState } from "react";
import { cn } from "../lib/cn";
import { Icon } from "./icon";
import type { SelectOption } from "./select";

export interface SearchSelectOption extends SelectOption {
	/**
	 * Дополнительные написания для поиска: английское имя страны, код ISO,
	 * эндоним языка. Человек ищет «Germany» и «DE» в русском интерфейсе не реже,
	 * чем «Германию», — а видит подпись на своём языке.
	 */
	keywords?: string;
}

export interface SearchSelectProps {
	options: readonly SearchSelectOption[];
	value?: string;
	onValueChange?: (value: string) => void;
	/** Подпись внутри поля, как у `Select` и `Input`. */
	label?: string;
	/** Текст пустого поля. Обязателен — у кита нет текстовых умолчаний. */
	placeholder: string;
	/** Подпись и плейсхолдер строки поиска («Найти страну»). */
	searchLabel: string;
	/** Что показать, когда не нашлось ничего. */
	emptyLabel: string;
	/**
	 * Подсказка под усечённым списком («уточните поиск»). Обязательна, как и
	 * остальные подписи кита: потолок включается у любого списка длиннее `limit`.
	 */
	refineLabel: string;
	/**
	 * Сколько пунктов показывать, пока запрос короче двух символов. Полный IANA
	 * (~450 зон) одним списком — это 450 кнопок на каждое открытие; тот, кому
	 * нужна зона, набирает её имя.
	 */
	limit?: number;
	size?: "md" | "sm";
	disabled?: boolean;
	name?: string;
	hasError?: boolean;
	className?: string;
}

/** Строка, по которой ищем: подпись + дополнительные написания + сам код. */
function haystackOf(option: SearchSelectOption): string {
	const label = typeof option.label === "string" ? option.label : "";
	return `${label} ${option.keywords ?? ""} ${option.value}`.toLocaleLowerCase();
}

/**
 * Поле выбора со строкой поиска — `Select` для длинных списков.
 *
 * Отдельный компонент, а не проп у `Select`: Radix Select держит свой
 * typeahead и переносит фокус на пункты, поэтому текстовое поле внутри его
 * контента воюет с ним за каждую клавишу. Здесь основа — Popover, фокус живёт
 * в поиске, а список остаётся списком (`listbox` + `aria-activedescendant`).
 *
 * Снаружи поле неотличимо от `Select`: та же геометрия, та же подпись внутри,
 * та же реакция на открытие. Список из двухсот стран без поиска — это не
 * «длинный список», а поле, которым нельзя пользоваться.
 */
export function SearchSelect({
	options,
	value,
	onValueChange,
	label,
	placeholder,
	searchLabel,
	emptyLabel,
	refineLabel,
	limit = 50,
	size = "md",
	disabled,
	name,
	hasError,
	className,
}: SearchSelectProps) {
	const listId = useId();
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [active, setActive] = useState(0);
	const listRef = useRef<HTMLDivElement>(null);

	const needle = query.trim().toLocaleLowerCase();
	const matched = useMemo(
		() => (needle ? options.filter((option) => haystackOf(option).includes(needle)) : options),
		[needle, options],
	);
	// Пока запрос короткий, выдачу режем; выбранное значение при этом остаётся
	// в списке — иначе открытое поле «теряет» свой же выбор.
	const truncated = needle.length < 2 && matched.length > limit;
	const visible = useMemo(() => {
		if (!truncated) return matched;
		const head = matched.slice(0, limit);
		const current = matched.find((option) => option.value === value);
		return current && !head.includes(current) ? [current, ...head] : head;
	}, [truncated, matched, limit, value]);

	const selected = options.find((option) => option.value === value);

	const commit = (next: string): void => {
		onValueChange?.(next);
		setOpen(false);
		setQuery("");
	};

	/** Клавиатура живёт на поле поиска: фокус не уходит из него ни на шаг. */
	const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
		if (event.key !== "ArrowDown" && event.key !== "ArrowUp" && event.key !== "Enter") return;
		if (visible.length === 0) return;
		event.preventDefault();

		if (event.key === "Enter") {
			const option = visible[active];
			if (option && !option.disabled) commit(option.value);
			return;
		}

		const next =
			event.key === "ArrowDown"
				? Math.min(active + 1, visible.length - 1)
				: Math.max(active - 1, 0);
		setActive(next);
		// Подсветка должна оставаться видимой: список скроллится сам, а стрелки
		// уводят активный пункт за край окна поповера.
		listRef.current?.children[next]?.scrollIntoView({ block: "nearest" });
	};

	return (
		<RadixPopover.Root
			open={open}
			onOpenChange={(next) => {
				setOpen(next);
				if (!next) setQuery("");
				if (next)
					setActive(
						Math.max(
							0,
							visible.findIndex((option) => option.value === value),
						),
					);
			}}
		>
			{name ? <input type="hidden" name={name} value={value ?? ""} /> : null}
			<RadixPopover.Trigger
				disabled={disabled}
				aria-invalid={hasError || undefined}
				className={cn(
					"relative flex w-full items-center gap-2.5 bg-sunken text-left text-fg",
					"border border-transparent transition-[border-color,box-shadow] duration-150 ease-smooth",
					"data-[state=open]:border-brand data-[state=open]:shadow-[0_0_0_3px_var(--color-ring-halo)]",
					"focus-ring",
					"disabled:cursor-not-allowed disabled:opacity-60",
					size === "sm"
						? "rounded-field-sm px-2.5 py-1.5 t-body"
						: cn("rounded-card px-5 t-body", label ? "pt-[27px] pb-[17px]" : "py-[19px]"),
					hasError && "border-danger",
					className,
				)}
			>
				{label && size === "md" ? (
					<span className="absolute top-3.5 left-5 font-mono text-[10px] text-fg-muted uppercase leading-[1.2] tracking-[var(--ls-tight)]">
						{label}
					</span>
				) : null}
				<span className={cn("min-w-0 flex-grow truncate", !selected && "text-fg-muted")}>
					{selected ? selected.label : placeholder}
				</span>
				<span className="text-fg-muted">
					<Icon
						name="ChevronDown"
						size={20}
						className="transition-transform duration-[var(--dur-300)] ease-elegant"
					/>
				</span>
			</RadixPopover.Trigger>

			<RadixPopover.Portal>
				<RadixPopover.Content
					align="start"
					sideOffset={2}
					collisionPadding={8}
					aria-label={searchLabel}
					className={cn(
						// Выше диалога (`z-120`) — см. `select.tsx`: под ним список
						// не кликается.
						"z-130 w-[var(--radix-popover-trigger-width)] max-w-[var(--radix-popover-content-available-width)]",
						"rounded-card bg-card p-1 shadow-float outline-none",
						"data-[state=open]:[animation:iec-reveal_var(--dur-400)_var(--ease-elegant)]",
					)}
				>
					<div className="pb-1">
						<div className="relative flex items-center">
							<span className="pointer-events-none absolute left-3 inline-flex text-fg-muted">
								<Icon name="Search" size={16} />
							</span>
							{/* Автофокус здесь — то, ради чего компонент существует: поле
							    открывают, чтобы набрать первые буквы, а не чтобы листать. */}
							<input
								// biome-ignore lint/a11y/noAutofocus: фокус в поиске — смысл контрола
								autoFocus
								type="search"
								role="combobox"
								aria-expanded
								aria-controls={listId}
								aria-autocomplete="list"
								aria-label={searchLabel}
								placeholder={searchLabel}
								value={query}
								onChange={(event) => {
									setQuery(event.target.value);
									setActive(0);
								}}
								onKeyDown={onKeyDown}
								className={cn(
									"w-full rounded-field-sm bg-sunken py-1.5 pr-2.5 pl-9 t-body text-fg outline-none",
									"border border-transparent transition-[border-color,box-shadow] duration-150 ease-smooth",
									"focus:border-brand focus:shadow-[0_0_0_3px_var(--color-ring-halo)]",
								)}
							/>
						</div>
					</div>

					{/* Потолок высоты, а не весь список: двести строк разворачивают
					    поповер на весь экран и прячут форму под собой. Второй
					    предел — сколько места реально осталось до края экрана,
					    минус строка поиска над списком (3.5rem). */}
					<div
						ref={listRef}
						id={listId}
						role="listbox"
						aria-label={searchLabel}
						className="iec-scroll flex max-h-[min(260px,calc(var(--radix-popover-content-available-height)-3.5rem))] flex-col gap-0.5"
					>
						{visible.map((option, index) => (
							<button
								key={option.value}
								type="button"
								role="option"
								aria-selected={option.value === value}
								disabled={option.disabled}
								onMouseEnter={() => setActive(index)}
								onClick={() => commit(option.value)}
								className={cn(
									"iec-press-glyph iec-menu-item flex h-10 shrink-0 cursor-pointer items-center gap-2.5 rounded-control px-2.5 text-left t-body text-fg",
									"outline-none transition-colors duration-150 ease-smooth",
									"disabled:cursor-not-allowed disabled:opacity-60",
									index === active && "bg-sunken",
								)}
							>
								<span className="min-w-0 flex-grow truncate">{option.label}</span>
								{option.value === value ? (
									<span className="text-brand-text">
										<Icon name="Check" size={20} />
									</span>
								) : null}
							</button>
						))}
						{visible.length === 0 ? (
							<p className="px-2.5 py-3 t-meta text-fg-muted">{emptyLabel}</p>
						) : null}
						{truncated ? (
							<p role="status" className="px-2.5 py-3 t-meta text-fg-muted">
								{refineLabel}
							</p>
						) : null}
					</div>
				</RadixPopover.Content>
			</RadixPopover.Portal>
		</RadixPopover.Root>
	);
}
